import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { registrationOnChange } from '@/utils/validation';
import { useFormContext } from 'react-hook-form';
import { CameraIcon, CheckCircleIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { Button, Input, Select } from '@/components/common';
import {
  useConfirmVehicleVariant,
  useExternalVehicleLookup,
  useRegistrationOCR,
  useVehicleCheck,
  useVehicleRegistrationLookup,
} from '@/hooks';
import { VehicleDuplicateModal } from '@/components/policies/VehicleDuplicateModal';
import api from '@/services/api';
import type { RTOLookup } from '@/services/geography';
import { isCheckableVehicleNumber, normaliseVehicleNumber, type VehicleCheckResult } from '@/types';
import { RC_REGEX, type MotorQuoteFormData } from '../../schema';
import { formatDate } from '@/utils';

/**
 * B9 -- registration numbers already put through the vehicle duplicate check,
 * per wizard form. Keyed by the form's `control` (stable for the form's life)
 * rather than held in component state, because this step unmounts whenever
 * the broker moves on and must not re-ask about the same vehicle on return.
 */
const checkedRegistrationsByForm = new WeakMap<object, Set<string>>();

/**
 * Whether the RTO inferred from the plate's own prefix may be written.
 *
 * Order of trust, strongest first: an RTO a human picked, then the registry's
 * actual registering office, then the prefix — which is only ever an inference.
 * The inference may still replace an EARLIER inference, because a new plate
 * describes a different vehicle and the office it named is now stale.
 *
 * This matters more than it looks: the RTO sets the zone and the zone sets the
 * premium, so a field that quietly fills itself with the wrong office reprices
 * the quote and nobody re-reads it.
 *
 * Exported so the rule can be unit-tested on its own; it is the only part of
 * this step worth testing and the only part that is pure.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function shouldApplyInferredRto({
  inferredCode,
  registryCode,
  currentCode,
  lastInferredCode,
}: {
  inferredCode?: string | null;
  registryCode?: string | null;
  currentCode?: string | null;
  lastInferredCode?: string | null;
}): boolean {
  if (!inferredCode) return false;
  // The registry names the office the vehicle is actually registered at; the
  // prefix only implies one.
  if (registryCode) return false;
  // Anything in the field that we did not put there is somebody's decision.
  if (currentCode && currentCode !== lastInferredCode) return false;
  // Already correct — writing it again would loop through watch().
  return currentCode !== inferredCode;
}

/**
 * Whether an inference of ours has gone stale and should be withdrawn.
 *
 * A broker who mistypes a plate, sees the wrong car and corrects it to one with
 * no derivable RTO — a letterless legacy number like `MH121234`, or a prefix
 * our master does not carry — must not be left holding the FIRST vehicle's
 * office. Only our own guess is ever withdrawn: a value we did not write is the
 * registry's or a human's, and neither is ours to clear.
 *
 * Reach, verified by running the real component: this covers every plate that
 * PASSES `RC_REGEX`, because only those enable the query that can settle with
 * nothing. A BH-series number (`22BH1234AA`) does NOT pass `RC_REGEX` — it
 * opens with digits — so its query never runs, `isSuccess` never turns true,
 * and a stale guess survives a correction to a BH plate. Left as is
 * deliberately: making the query fire on ineligible input would also fire it
 * mid-typing, and `currentCode === lastInferredCode` is only a proxy for "ours"
 * (a human who re-picks the same office by hand looks identical), so widening
 * the window trades a rare stale value for a rarer clobbered human choice.
 */
// eslint-disable-next-line react-refresh/only-export-components
export function shouldClearStaleInferredRto({
  inferredCode,
  inferenceSettled,
  currentCode,
  lastInferredCode,
}: {
  inferredCode?: string | null;
  inferenceSettled: boolean;
  currentCode?: string | null;
  lastInferredCode?: string | null;
}): boolean {
  // In flight, "no code" means "not yet", not "none".
  if (!inferenceSettled || inferredCode) return false;
  return !!currentCode && currentCode === lastInferredCode;
}

/**
 * Upfront vehicle lookup (Wave F2 item 3b) — moved earlier in MOTOR_STEPS
 * (schema.ts) specifically so this step can run a lookup against our own
 * issued policies/customer vehicles as soon as a valid registration number
 * is entered, and PREFILL make/model/variant/registration-date/manufacturing
 * -year/RTO (and, for Renewal/Rollover, the Prior Policy step's fields) —
 * letting the user just confirm each downstream step instead of re-typing
 * everything. Never blocks manual entry: a miss (found=false) is silent.
 */
export function RegistrationNumberStep() {
  const {
    register,
    watch,
    setValue,
    trigger,
    control,
    formState: { errors },
  } = useFormContext<MotorQuoteFormData>();

  const businessType = watch('business_type');
  const registrationPending = watch('registration_pending');
  const registrationNumber = watch('registration_number') || '';
  const isNew = businessType === 'new';
  const required = isNew ? !registrationPending : true;

  const [debouncedReg, setDebouncedReg] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedReg(registrationNumber.trim().toUpperCase());
    }, 400);
    return () => clearTimeout(timer);
  }, [registrationNumber]);

  const lookupEnabled = !(isNew && registrationPending) && RC_REGEX.test(debouncedReg);
  // The broker's correction when our best guess was the wrong car. Cleared
  // whenever the registration changes, since it described a different vehicle.
  const [correctedModelId, setCorrectedModelId] = useState<string>('');
  const { data: lookupResult } = useVehicleRegistrationLookup(
    debouncedReg,
    lookupEnabled,
    correctedModelId || undefined,
  );
  const [showModelPicker, setShowModelPicker] = useState(false);
  const modelOptions = lookupResult?.vehicle?.model_options ?? [];

  // B-04: which number the engine/chassis below were captured for. A ref, not
  // form state, so remounting the step (moving back to it) does not read as
  // "the number changed" and wipe what the lookup found.
  const identifiersForRef = useRef<string | null>(null);

  useEffect(() => {
    setCorrectedModelId('');
    setShowModelPicker(false);
    // A different vehicle: the previous car's engine/chassis must not ride
    // along to this one's quote when its own lookup finds nothing.
    if (debouncedReg && identifiersForRef.current && identifiersForRef.current !== debouncedReg) {
      setValue('engine_number', '');
      setValue('chassis_number', '');
      identifiersForRef.current = null;
    }
  }, [debouncedReg]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── B9 (e): vehicle duplicate check ──────────────────────────────────────
  // Same debounced number as the lookup, once per distinct number. Skipped
  // when this quote already renews a policy (arrived via `?renewal=`, or the
  // broker already chose), and for a new vehicle still awaiting registration.
  const previousPolicyId = watch('previous_policy_id') || '';
  const vehicleCheck = useVehicleCheck();
  const [vehicleCheckResult, setVehicleCheckResult] = useState<VehicleCheckResult | null>(null);
  const [vehicleCheckFailed, setVehicleCheckFailed] = useState(false);
  const checkedRegistrations = useMemo(() => {
    let set = checkedRegistrationsByForm.get(control);
    if (!set) {
      set = new Set<string>();
      checkedRegistrationsByForm.set(control, set);
    }
    return set;
  }, [control]);
  const latestCheckedRegRef = useRef('');
  // An answer that lands after this step unmounted is dropped: nobody saw it.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);
  const vehicleCheckSkipped = !!previousPolicyId || (isNew && !!registrationPending);

  useEffect(() => {
    const norm = normaliseVehicleNumber(debouncedReg);
    latestCheckedRegRef.current = norm;
    // A prompt about the previous number no longer describes this vehicle.
    setVehicleCheckResult(null);
    setVehicleCheckFailed(false);
    if (vehicleCheckSkipped || !RC_REGEX.test(debouncedReg)) return;
    if (!isCheckableVehicleNumber(norm) || checkedRegistrations.has(norm)) return;
    // `mutateAsync`, not `mutate` with per-call callbacks: TanStack Query does
    // not run those once the calling component has unmounted, and the broker
    // may well click Next before the answer lands. The number is remembered
    // as checked only once its answer has actually been shown, so a dropped
    // answer is asked again when the broker comes back to this step.
    vehicleCheck
      .mutateAsync({ vehicleNumber: norm })
      .then((result) => {
        if (!mountedRef.current || latestCheckedRegRef.current !== norm) return;
        checkedRegistrations.add(norm);
        if (result?.checked && result.matches?.length) setVehicleCheckResult(result);
      })
      .catch(() => {
        if (!mountedRef.current || latestCheckedRegRef.current !== norm) return;
        // Advisory only: say it, never block the quote.
        setVehicleCheckFailed(true);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedReg, vehicleCheckSkipped]);

  /** Quote this as the renewal of `policyId`. */
  const linkRenewal = (policyId: string) => {
    setValue('business_type', 'renewal', { shouldValidate: true, shouldDirty: true });
    setValue('previous_policy_id', policyId, { shouldValidate: true, shouldDirty: true });
    setVehicleCheckResult(null);
  };

  /**
   * The RTO the plate's own prefix names (MH12AB1234 -> MH12), read straight
   * off our RTO master. The registry lookup above also fills this field, but
   * only when Protean answers — and Protean has not allowlisted our egress IP,
   * so in production it never does and the field stays blank. This costs one
   * local query, reaches no vendor, and returns nothing at all when the prefix
   * is not unambiguous (a BH-series plate carries no RTO anywhere in it).
   */
  const { data: inferredRto, isSuccess: inferenceSettled } = useQuery({
    queryKey: ['rtos', 'from-registration', debouncedReg],
    queryFn: async () => {
      const { data } = await api.get<{ results: RTOLookup[] }>('/geography/rtos/search/', {
        params: { registration: debouncedReg },
      });
      return data.results[0] ?? null;
    },
    enabled: lookupEnabled,
    // A plate's prefix never changes, so this is answerable once per number.
    staleTime: Infinity,
  });

  const rtoCode = watch('rto_code');
  const lastInferredRtoRef = useRef<string | null>(null);

  useEffect(() => {
    const decision = {
      inferredCode: inferredRto?.code,
      registryCode: lookupResult?.found ? lookupResult.vehicle?.rto_code : null,
      currentCode: rtoCode,
      lastInferredCode: lastInferredRtoRef.current,
    };

    if (inferredRto && shouldApplyInferredRto(decision)) {
      lastInferredRtoRef.current = inferredRto.code;
      // Not `shouldDirty` — dirtiness is how the RTO step's own picker is told
      // apart from a field that filled itself.
      setValue('rto_code', inferredRto.code, { shouldValidate: true });
      setValue('rto_label', `${inferredRto.code} — ${inferredRto.name}`);
      return;
    }

    if (shouldClearStaleInferredRto({ ...decision, inferenceSettled })) {
      lastInferredRtoRef.current = null;
      setValue('rto_code', '', { shouldValidate: true });
      setValue('rto_label', '');
    }
  }, [inferredRto, inferenceSettled, lookupResult, rtoCode, setValue]);

  const [matchedSource, setMatchedSource] =
    useState<'policy' | 'customer_vehicle' | 'vehicle_registry' | null>(null);
  const externalLookup = useExternalVehicleLookup();
  const confirmVariant = useConfirmVehicleVariant();
  const ocr = useRegistrationOCR();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [ocrCandidates, setOcrCandidates] = useState<string[]>([]);
  // Already ordered best-first by the server against `variant_hint`.
  const variantOptions = lookupResult?.vehicle?.variant_options ?? [];
  const suggestedVariantId = lookupResult?.vehicle?.suggested_variant_id ?? null;
  const appliedForRef = useRef<string | null>(null);

  const applyRegistrationNumber = (value: string) => {
    setValue('registration_number', value, { shouldValidate: true, shouldDirty: true });
    setDebouncedReg(value.trim().toUpperCase());
    setOcrCandidates([]);
  };

  /**
   * The broker has decided which variant this is. Tell the server, which
   * remembers it against the vendor's make/model strings so the next broker
   * to meet the same wording gets it resolved for free.
   */
  const chooseVariant = (variantId: string) => {
    const chosen = variantOptions.find((v) => v.id === variantId);
    setValue('variant_id', variantId, { shouldValidate: true, shouldDirty: true });
    setValue('variant_name', chosen?.name || '');
    if (variantId && debouncedReg && lookupResult?.source === 'vehicle_registry') {
      confirmVariant.mutate({ registrationNumber: debouncedReg, variantId });
    }
  };

  useEffect(() => {
    if (!lookupResult?.found || !lookupResult.vehicle) return;
    // Apply once per distinct match — never re-fires just from unrelated
    // re-renders, and never overwrites a match once already applied for the
    // same debounced registration number. A correction is a distinct match:
    // it names a different car, so it must be applied over the first guess.
    const applyKey = `${debouncedReg}:${correctedModelId}`;
    if (appliedForRef.current === applyKey) return;
    appliedForRef.current = applyKey;

    const v = lookupResult.vehicle;
    if (v.vehicle_category) setValue('vehicle_category', v.vehicle_category as MotorQuoteFormData['vehicle_category'], { shouldValidate: true });
    if (v.make_id) {
      setValue('make_id', v.make_id, { shouldValidate: true, shouldDirty: true });
      setValue('make_name', v.make_name || '');
    }
    if (v.model_id) {
      setValue('model_id', v.model_id, { shouldValidate: true, shouldDirty: true });
      setValue('model_name', v.model_name || '');
    }
    if (v.variant_id) {
      setValue('variant_id', v.variant_id, { shouldValidate: true, shouldDirty: true });
      setValue('variant_name', v.variant_name || '');
    }
    if (v.fuel_type) setValue('fuel_type', v.fuel_type);
    if (v.manufacturing_year) setValue('manufacturing_year', String(v.manufacturing_year), { shouldValidate: true });
    if (v.registration_date) setValue('registration_date', v.registration_date, { shouldValidate: true });
    if (v.rto_code) {
      setValue('rto_code', v.rto_code, { shouldValidate: true });
      setValue('rto_label', v.rto_label || v.rto_code);
    }
    // B-04: the RC record's engine and chassis numbers. Nothing on this step
    // shows them, but they travel with the quote to the policy it becomes --
    // before this they were answered by the lookup and dropped on the floor.
    // Set even when blank, so a correction never keeps the first guess's.
    setValue('engine_number', v.engine_number || '');
    setValue('chassis_number', v.chassis_number || '');
    identifiersForRef.current = debouncedReg;

    // Prior-policy prefill only matters for Renewal/Rollover — harmless to
    // set for New too (PriorPolicyStep just never renders to show it).
    const prior = lookupResult.prior_policy;
    if (prior) {
      if (prior.previous_policy_number) setValue('previous_policy_number', prior.previous_policy_number);
      if (prior.previous_insurer_id) {
        setValue('previous_insurer_id', prior.previous_insurer_id, { shouldValidate: true });
        setValue('previous_insurer_name', prior.previous_insurer_name || '');
      }
      if (prior.previous_policy_expiry) setValue('previous_policy_expiry', prior.previous_policy_expiry, { shouldValidate: true });
      if (prior.previous_ncb !== null && prior.previous_ncb !== undefined) {
        setValue('previous_ncb', String(prior.previous_ncb));
      }
      setValue('has_claim_in_previous', prior.has_claim_in_previous ? 'yes' : 'no');
    }

    setMatchedSource(lookupResult.source ?? null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lookupResult, debouncedReg, correctedModelId]);

  return (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
        Vehicle Registration Number
      </h2>
      <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 mb-6">
        {isNew
          ? 'Enter the registration number, or mark it pending if the vehicle has not been registered yet.'
          : 'Enter the existing registration number of the vehicle.'}
      </p>

      {isNew && (
        <label className="flex items-center gap-3 cursor-pointer mb-4">
          <input
            type="checkbox"
            checked={!!registrationPending}
            onChange={(e) => {
              setValue('registration_pending', e.target.checked, { shouldDirty: true });
              void trigger('registration_number');
            }}
            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
          />
          <span className="text-sm text-gray-700 dark:text-gray-300">Registration Pending</span>
        </label>
      )}

      <div className="sm:max-w-sm">
        <Input
          label="Registration Number"
          placeholder="e.g. MH01AB1234"
          disabled={isNew && !!registrationPending}
          error={errors.registration_number?.message}
          helperText="Case-insensitive — normalised to uppercase on submit."
          required={required}
          {...register('registration_number', { onChange: registrationOnChange })}
        />
        {vehicleCheckFailed && (
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Could not check this vehicle for existing policies.
          </p>
        )}

        {/* Typing a plate on a phone, next to the car, is the awkward part.
            Reading it off a photo costs nothing and reaches no vendor. */}
        {!(isNew && registrationPending) && (
          <div className="mt-2">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                e.target.value = '';
                if (!file) return;
                ocr.mutate(file, {
                  onSuccess: (data) => {
                    // One clear reading applies itself; several stay a choice,
                    // because OCR confuses 0/O and 8/B and the wrong number
                    // would look at another person's car.
                    if (data.candidates.length === 1) {
                      applyRegistrationNumber(data.candidates[0]);
                    } else {
                      setOcrCandidates(data.candidates);
                    }
                  },
                });
              }}
            />
            <Button
              type="button"
              size="sm"
              variant="ghost"
              disabled={ocr.isPending}
              onClick={() => fileInputRef.current?.click()}
            >
              <CameraIcon className="mr-1 h-4 w-4" />
              {ocr.isPending ? 'Reading…' : 'Scan the number plate'}
            </Button>

            {ocr.isSuccess && !ocr.isPending && ocrCandidates.length === 0 && !ocr.data?.found && (
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Could not read a number from that photo — please type it in.
              </p>
            )}

            {ocrCandidates.length > 0 && (
              <div className="mt-2">
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Which one is it?
                </p>
                <div className="mt-1 flex flex-wrap gap-2">
                  {ocrCandidates.map((candidate) => (
                    <Button
                      key={candidate}
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => applyRegistrationNumber(candidate)}
                    >
                      {candidate}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {matchedSource && (
        <div className="mt-4 sm:max-w-lg flex items-start gap-2 p-4 rounded-lg border border-green-200 bg-green-50 dark:bg-green-900/20 dark:border-green-700 text-sm text-green-800 dark:text-green-300">
          <CheckCircleIcon className="w-5 h-5 flex-shrink-0" />
          <div>
            <span>
              {matchedSource === 'policy'
                ? 'Found an existing policy for this vehicle — make, model and prior policy details have been prefilled below.'
                : matchedSource === 'customer_vehicle'
                  ? "Found this vehicle on the customer's record — details have been prefilled below."
                  : 'Found this vehicle in our records — details have been prefilled below.'}{' '}
              Review each step and adjust anything that has changed.
            </span>
            {/* B9: the matched policy can be renewed from here in one tap. */}
            {matchedSource === 'policy' && lookupResult?.prior_policy?.policy_id && (
              previousPolicyId === lookupResult.prior_policy.policy_id ? (
                <div className="mt-1 text-xs font-medium">This quote renews that policy.</div>
              ) : !previousPolicyId ? (
                <div className="mt-1">
                  <button
                    type="button"
                    className="text-xs font-medium underline hover:no-underline"
                    onClick={() => linkRenewal(lookupResult.prior_policy!.policy_id!)}
                  >
                    Renew this policy
                  </button>
                </div>
              ) : null
            )}
            {lookupResult?.verified_at && (
              <div className="mt-1 text-xs opacity-80">
                From our own records, checked{' '}
                {formatDate(lookupResult.verified_at)}
                {lookupResult.is_stale && ' — over a year old.'}
              </div>
            )}
            {/* What we matched, and a way out of a wrong match. The master
                carries near-duplicate makes, so the right car is sometimes
                the second candidate — without this the broker would abandon
                the lookup and type the vehicle by hand. */}
            {lookupResult?.vehicle?.model_name && modelOptions.length > 1 && (
              <div className="mt-2 text-xs">
                <span className="opacity-80">
                  Matched as {lookupResult.vehicle.make_name} {lookupResult.vehicle.model_name}.
                </span>{' '}
                <button
                  type="button"
                  className="underline hover:no-underline"
                  onClick={() => setShowModelPicker((open) => !open)}
                >
                  {showModelPicker ? 'Keep this' : 'Not this vehicle?'}
                </button>

                {showModelPicker && (
                  <div className="mt-2 max-w-xs">
                    <Select
                      label="Which model is it?"
                      options={[
                        { value: '', label: 'Use the closest match' },
                        ...modelOptions.map((m) => ({
                          value: m.id,
                          label: m.make_name ? `${m.make_name} ${m.name}` : m.name,
                        })),
                      ]}
                      value={correctedModelId}
                      onChange={(e) => {
                        // Choosing a different model changes the car, so the
                        // variant chosen under the old one no longer applies.
                        setValue('variant_id', '', { shouldDirty: true });
                        setValue('variant_name', '');
                        setCorrectedModelId(e.target.value);
                      }}
                    />
                  </div>
                )}
              </div>
            )}

            {lookupResult?.needs_variant_choice && variantOptions.length > 0 && (
              <div className="mt-3 max-w-xs">
                {/* Ordered best-first, and the likely one is labelled — but
                    nothing is pre-selected. The variant sets the premium, so
                    the broker's tap is what makes it a decision. */}
                <Select
                  label="Which variant?"
                  options={[
                    { value: '', label: 'Select the variant…' },
                    ...variantOptions.map((v) => ({
                      value: v.id,
                      label: v.id === suggestedVariantId ? `${v.name} — likely` : v.name,
                    })),
                  ]}
                  value={watch('variant_id') || ''}
                  onChange={(e) => chooseVariant(e.target.value)}
                />
                {lookupResult.vehicle?.variant_hint && (
                  <p className="mt-1 text-xs opacity-80">
                    The record says “{lookupResult.vehicle.variant_hint}”.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Nothing of ours matches. Reaching a vendor costs money, so it happens
          on a click and never on a keystroke. */}
      {lookupEnabled && lookupResult && !lookupResult.found && lookupResult.external_available && (
        <div className="mt-4 sm:max-w-lg rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50">
          <p className="text-sm text-gray-700 dark:text-gray-300">
            We have no record of {debouncedReg}. You can search official RC records for it,
            or simply enter the details yourself on the next steps.
          </p>
          <Button
            size="sm"
            variant="outline"
            className="mt-3"
            disabled={externalLookup.isPending}
            onClick={() => externalLookup.mutate(debouncedReg)}
          >
            <MagnifyingGlassIcon className="mr-1 h-4 w-4" />
            {externalLookup.isPending ? 'Searching…' : 'Search RC records'}
          </Button>
        </div>
      )}

      {vehicleCheckResult && (
        <VehicleDuplicateModal
          result={vehicleCheckResult}
          onRenew={linkRenewal}
          onNewAnyway={() => setVehicleCheckResult(null)}
          onClose={() => setVehicleCheckResult(null)}
        />
      )}
    </div>
  );
}
